import React, { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import { useDispatch, useSelector } from "react-redux";
import { useAdminUsers } from "../../hooks/useAdminUsers";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import AdminUserFilters from "../AdminUserFilters/AdminUserFilters";
import { Button } from "@/components/Button/Button";
import { AdminUser } from "../../api/fetchAdminUsers";
import { createAdminUserColumns } from "../AdminUserColumns/AdminUserColumns";
import DataTable from "@/components/Datatable/Datatable";
import { SyncLogsModal } from "../SyncLogsModal/SyncLogsModal";
import DeactivateUserModal from "../DeactivateUserModal/DeactivateUserModal";
import ReactivateUserModal from "../ReactivateUserModal/ReactivateUserModal";
import { useToast } from "@/components/ui/use-toast";
import { triggerEmployeeSync } from "@/api/remoteSync/remoteSyncApi";
import { deactivateUser } from "../../api/deactivateUser/deactivateUserApi";
import { reactivateUser } from "../../api/reactivateUser/reactivateUserApi";
import {
  addSyncingEmployee,
  selectSyncingEmployeeIds,
} from "../../slices/adminUsersSlice";
import { EmployeeSyncSubscriptions } from "../../hooks/useEmployeeSyncSubscriptions";
import { startCrossAppImpersonation } from "@/api/impersonation/impersonationApi";

const AdminUserList: React.FC = () => {
  const {
    users,
    totalCount,
    loading,
    error,
    pagination,
    sorting,
    handlePaginationChange,
    handleSortingChange,
    handleFilterChange,
    filters,
    refetch,
  } = useAdminUsers();

  const navigate = useNavigate();
  const { getAccessTokenSilently } = useAuth0();
  const dispatch = useDispatch();
  const { toast } = useToast();

  const syncingEmployeeIds = useSelector(selectSyncingEmployeeIds);

  const [selectedUserForSyncLogs, setSelectedUserForSyncLogs] =
    useState<AdminUser | null>(null);
  const [isSyncLogsModalOpen, setIsSyncLogsModalOpen] = useState(false);
  const [userToDeactivate, setUserToDeactivate] = useState<AdminUser | null>(
    null,
  );
  const [isDeactivating, setIsDeactivating] = useState(false);
  const [userToReactivate, setUserToReactivate] = useState<AdminUser | null>(
    null,
  );
  const [isReactivating, setIsReactivating] = useState(false);

  const handleEdit = useCallback(
    (uuid: string) => {
      navigate(`/admin/users/${uuid}/edit`);
    },
    [navigate],
  );

  const handleImpersonateUser = useCallback(
    async (employeeUuid: string) => {
      await startCrossAppImpersonation(employeeUuid);
      await getAccessTokenSilently({ cacheMode: "off" });
      localStorage.removeItem("selectedCompanyUuid");
      // Hard navigation to root: this re-bootstraps userAppSettings/permissions
      // (App.tsx) for the impersonated user. A soft navigate would leave the
      // impersonator's permissions stale in Redux.
      window.location.href = "/";
    },
    [getAccessTokenSilently],
  );

  const handleViewSyncLogs = useCallback((user: AdminUser) => {
    setSelectedUserForSyncLogs(user);
    setIsSyncLogsModalOpen(true);
  }, []);

  const handleSyncUser = useCallback(
    async (user: AdminUser) => {
      if (!user.employeeId) {
        toast({
          title: "Error",
          description: "Employee ID not found for this user.",
          variant: "destructive",
        });
        return;
      }

      try {
        await triggerEmployeeSync(user.employeeId);
        // Start tracking this employee for real-time sync updates
        dispatch(addSyncingEmployee(user.employeeId));
        toast({
          title: "Sync Initiated",
          description: `Sync started for ${user.firstName} ${user.lastName}`,
        });
      } catch (error) {
        toast({
          title: "Sync Failed",
          description: "Failed to initiate sync. Please try again.",
          variant: "destructive",
        });
      }
    },
    [toast, dispatch],
  );

  const handleDeactivateUser = useCallback((user: AdminUser) => {
    setUserToDeactivate(user);
  }, []);

  const handleReactivateUser = useCallback((user: AdminUser) => {
    setUserToReactivate(user);
  }, []);

  const confirmDeactivateUser = useCallback(async () => {
    if (!userToDeactivate) {
      return;
    }

    if (!userToDeactivate.employeeUuid) {
      toast({
        title: "Error",
        description: "Employee UUID not found for this user.",
        variant: "destructive",
      });
      return;
    }

    setIsDeactivating(true);
    try {
      await deactivateUser(userToDeactivate.employeeUuid);
      toast({
        title: "User Deactivated",
        description: `${userToDeactivate.firstName} ${userToDeactivate.lastName} can no longer log in.`,
      });
      setUserToDeactivate(null);
      refetch();
    } catch (error) {
      toast({
        title: "Deactivation Failed",
        description: "Failed to deactivate user. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsDeactivating(false);
    }
  }, [userToDeactivate, toast, refetch]);

  const confirmReactivateUser = useCallback(async () => {
    if (!userToReactivate) {
      return;
    }

    if (!userToReactivate.employeeUuid) {
      toast({
        title: "Error",
        description: "Employee UUID not found for this user.",
        variant: "destructive",
      });
      return;
    }

    setIsReactivating(true);
    try {
      await reactivateUser(userToReactivate.employeeUuid);
      toast({
        title: "User Reactivated",
        description: `${userToReactivate.firstName} ${userToReactivate.lastName} has regained access.`,
      });
      setUserToReactivate(null);
      refetch();
    } catch (error) {
      toast({
        title: "Reactivation Failed",
        description: "Failed to reactivate user. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsReactivating(false);
    }
  }, [userToReactivate, toast, refetch]);

  const columns = useMemo(
    () =>
      createAdminUserColumns({
        handleEdit,
        handleImpersonateUser,
        handleViewSyncLogs,
        handleSyncUser,
        handleDeactivateUser,
        handleReactivateUser,
        syncingEmployeeIds,
        showCompany: !filters.companyUuid,
      }),
    [
      handleEdit,
      handleImpersonateUser,
      handleViewSyncLogs,
      handleSyncUser,
      handleDeactivateUser,
      handleReactivateUser,
      syncingEmployeeIds,
      filters.companyUuid,
    ],
  );

  return (
    <MainPageWrapper
      actions={
        <Button onClick={() => navigate("/admin/users/create")}>
          Create User
        </Button>
      }
      error={error}
      title="User Management"
    >
      <AdminUserFilters
        filters={filters}
        onFilterChange={(
          filterKey: string,
          value: string | number | undefined,
        ) => {
          handleFilterChange(filterKey, value);
          handlePaginationChange({
            pageIndex: 0,
            pageSize: pagination.pageSize,
          });
        }}
      />

      <div className="relative">
        <DataTable<AdminUser>
          columns={columns}
          data={users}
          error={error}
          loading={loading}
          noDataMessage="No users found"
          onPageChange={(newPageIndex, newPageSize) =>
            handlePaginationChange({
              pageIndex: newPageIndex,
              pageSize: newPageSize,
            })
          }
          onSortingChange={handleSortingChange}
          pageIndex={pagination.pageIndex}
          pageSize={pagination.pageSize}
          rowKeyExtractor={(item) => item.employeeUuid}
          sorting={sorting}
          totalCount={totalCount}
        />
      </div>

      <SyncLogsModal
        employeeId={selectedUserForSyncLogs?.employeeId ?? null}
        isOpen={isSyncLogsModalOpen}
        onClose={() => setIsSyncLogsModalOpen(false)}
        user={selectedUserForSyncLogs}
      />

      <DeactivateUserModal
        handleDeactivate={confirmDeactivateUser}
        isDeactivating={isDeactivating}
        isOpen={userToDeactivate !== null}
        onClose={() => setUserToDeactivate(null)}
        userName={
          userToDeactivate
            ? `${userToDeactivate.firstName} ${userToDeactivate.lastName}`
            : ""
        }
      />

      <ReactivateUserModal
        handleReactivate={confirmReactivateUser}
        isOpen={userToReactivate !== null}
        isReactivating={isReactivating}
        onClose={() => setUserToReactivate(null)}
        userName={
          userToReactivate
            ? `${userToReactivate.firstName} ${userToReactivate.lastName}`
            : ""
        }
      />

      {/* Subscription handlers for real-time sync status updates */}
      <EmployeeSyncSubscriptions />
    </MainPageWrapper>
  );
};

export default AdminUserList;
